import { useState, type FormEvent } from 'react';
import type { Note, NoteListQuery } from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { useNotes, useCreateNote, useUpdateNote, useDeleteNote } from '../../api/notes';

interface Props {
  title?: string;
  query: NoteListQuery;
  attachTo: { contactId?: string; leadId?: string };
}

function NoteItem({ note, canManage }: { note: Note; canManage: boolean }) {
  const update = useUpdateNote();
  const del = useDeleteNote();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);

  return (
    <div style={{ padding: 'var(--space-3) 0', borderBottom: '1px solid var(--color-border)' }}>
      {editing ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <textarea
            className="ne-input"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              disabled={update.isPending || draft.trim() === ''}
              onClick={async () => {
                try {
                  await update.mutateAsync({ id: note.id, body: draft.trim() });
                  setEditing(false);
                  toast.success('Note updated.');
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Could not update the note.');
                }
              }}
            >
              Save
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDraft(note.body);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{note.body}</p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-1)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
            }}
          >
            <span>
              {note.author?.displayName ?? 'Someone'} · {new Date(note.createdAt).toLocaleString()}
            </span>
            <span style={{ flex: 1 }} />
            {canManage ? (
              <>
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary)',
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() =>
                    del.mutate(note.id, {
                      onSuccess: () => toast.success('Note deleted.'),
                      onError: (err) =>
                        toast.error(
                          err instanceof Error ? err.message : 'Could not delete the note.',
                        ),
                    })
                  }
                  disabled={del.isPending}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-danger)',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export function NoteList({ title = 'Notes', query, attachTo }: Props) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('notes.manage');
  const notes = useNotes({ pageSize: 200, sortBy: 'createdAt', sortOrder: 'desc', ...query });
  const create = useCreateNote();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    const b = body.trim();
    if (b.length < 1) {
      setFieldError('Enter a note.');
      return;
    }
    if (b.length > 10_000) {
      setFieldError('Note must be 10,000 characters or fewer.');
      return;
    }
    setFieldError(undefined);
    try {
      await create.mutateAsync({ body: b, ...attachTo });
      setBody('');
      toast.success('Note added.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (serverFieldErrors.body) {
          setFieldError(serverFieldErrors.body);
          toast.error('Please fix the highlighted field.');
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : 'Could not add the note.');
    }
  }

  return (
    <Card title={title}>
      {canManage ? (
        <form
          onSubmit={onAdd}
          style={{ display: 'grid', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}
          noValidate
        >
          <div className="ne-field">
            <label className="ne-field__label" htmlFor="note-body">
              Note
            </label>
            <textarea
              id="note-body"
              className={['ne-input', fieldError ? 'ne-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              rows={2}
              placeholder="Write a note…"
              value={body}
              aria-invalid={fieldError ? true : undefined}
              onChange={(e) => {
                setBody(e.target.value);
                setFieldError(undefined);
              }}
            />
            {fieldError ? (
              <span className="ne-field__error" role="alert">
                {fieldError}
              </span>
            ) : null}
          </div>
          <div>
            <Button type="submit" disabled={create.isPending || body.trim() === ''}>
              Add note
            </Button>
          </div>
        </form>
      ) : null}

      {notes.isPending ? (
        <PageState kind="loading" message="Loading notes…" />
      ) : notes.isError ? (
        <PageState
          kind="error"
          message={notes.error.message}
          onRetry={() => void notes.refetch()}
        />
      ) : notes.data.items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No notes yet.</p>
      ) : (
        <div>
          {notes.data.items.map((note) => (
            <NoteItem key={note.id} note={note} canManage={canManage} />
          ))}
        </div>
      )}
    </Card>
  );
}
